import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createAddress, deleteAddress, fetchAddresses, updateAddress } from '../api/addresses'
import type { AddressDraft, SavedAddress } from '../constants/addresses'
import { QUERY_KEYS } from '../constants/query'
import { removeSavedAddress, upsertSavedAddress } from '../helpers/addresses'
import { useAuth } from './useAuth'

export function useSavedAddresses() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: QUERY_KEYS.addresses,
    queryFn: fetchAddresses,
    enabled: Boolean(user),
  })

  async function save(input: AddressDraft, id?: string): Promise<SavedAddress> {
    const next = id ? await updateAddress(id, input) : await createAddress(input)
    queryClient.setQueryData<SavedAddress[]>(QUERY_KEYS.addresses, (current) =>
      upsertSavedAddress(current, next),
    )
    return next
  }

  async function remove(id: string): Promise<void> {
    await deleteAddress(id)
    queryClient.setQueryData<SavedAddress[]>(QUERY_KEYS.addresses, (current) =>
      removeSavedAddress(current, id),
    )
  }

  return {
    addresses: query.data ?? [],
    loading: query.isPending && !query.data,
    save,
    remove,
  }
}
